import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Filter } from "lucide-react";

export function MultiSelect<T extends string>({
  label,
  options,
  values,
  setValues,
  closeOnSelect = false,
  onOpenChange,
}: {
  label: string;
  options: readonly T[];
  values: T[];
  setValues: (arr: T[]) => void;
  closeOnSelect?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleButtonClick = () => {
    const newOpen = !open;
    setOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  const toggle = (v: T) => {
    const newValues = values.includes(v) ? values.filter((x) => x !== v) : [...values, v];
    setValues(newValues);
    // Chiudi il menu dopo la selezione solo se closeOnSelect è true
    if (closeOnSelect) {
      setOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={handleButtonClick}
        className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm hover:bg-gray-50"
      >
        <Filter className="h-4 w-4" /> {label} <ChevronDown className="h-4 w-4 opacity-60" />
      </button>

      {open && (
        <div className="absolute z-20 mt-2 w-56 rounded-xl border border-gray-200 bg-white p-2 shadow-lg">
          <div className="max-h-64 space-y-1 overflow-auto p-1">
            {options.map((opt) => (
              <label key={opt} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={values.includes(opt)}
                  onChange={() => toggle(opt)}
                />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
          {values.length > 0 && (
            <div className="flex items-center justify-between border-t border-gray-100 p-2">
              <button className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setValues([])}>
                Pulisci
              </button>
              <button className="text-xs text-blue-600 hover:text-blue-700" onClick={() => setOpen(false)}>
                Chiudi
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
